import {
  Box,
  Button,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { type Account } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { URLS } from '@/components/accounts/useCreateAwsAccount';
import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { docsBaseUrl } from '@/contexts/environment';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { useSyncAccountPermissions } from './useSyncAccountPermissions';
import { openUrlInNewWindow } from './validateAccount';

import { Loader } from '../layout/loading';
import { PanelWrapper } from '../panels/panelWrapper';
import { ReadMoreLink } from '../shared/readMoreLink';

export const UpdateAccountAWSPanel = ({
  account,
  onUpdated,
  onClose,
}: {
  account: Account;
  onUpdated: () => void;
  onClose: () => void;
}) => {
  const [isLoadingStackArn, setLoadingStackArn] = useState(true);
  const [isStackUpdateInProgress, setStackUpdateInProgress] = useState(false);
  const [isValidationInProgress, setValidationInProgress] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { syncAccountPermissions } = useSyncAccountPermissions(
    onUpdated,
    setValidationInProgress,
    setHasError,
    account
  );
  const [stackState, setStackState] = useState({
    stackId: '',
    stackName: '',
    region: '',
  });
  const [triggerFetchStackId, setTriggerFetchStackId] = useState(false);
  const { currentProjectId } = useUser();

  const dal = useDAL();

  const templateUrl = URLS[account.accountType];

  const roleAccountId = account.providerAccountId;

  const onUpdateClicked = async () => {
    return syncAccountPermissions();
  };

  useEffect(() => {
    const fetchStackId = async () => {
      try {
        const response = await dal.cloudAccounts.stack.get(account.id);

        let region = '';
        let stackName = '';
        const stackId = response.stackId;
        const parts = stackId.split(':');
        if (
          parts.length >= 6 &&
          parts[0] === 'arn' &&
          parts[2] === 'cloudformation'
        ) {
          region = parts[3];

          const stackNameParts = parts[5].split('/');
          if (stackNameParts.length >= 2) {
            stackName = stackNameParts[1];
          }
        }
        setStackState({
          stackId,
          region,
          stackName,
        });
        setLoadingStackArn(false);
      } catch {
        setHasError(true);
      }
    };
    void fetchStackId();
  }, [
    dal.cloudAccounts.stack,
    currentProjectId,
    account.id,
    account.role,
    triggerFetchStackId,
  ]);

  const openAWSConsole = async () => {
    if (!stackState.stackId) {
      setHasError(true);
      return;
    }

    const url = `https://console.aws.amazon.com/cloudformation/home?region=${stackState.region}#/stacks/update/template?stackId=${stackState.stackId}`;
    setStackUpdateInProgress(true);
    await openUrlInNewWindow({
      url,
      title: 'AWSConsole',
      width: window.screen.width * 0.8,
      height: window.screen.height * 0.8,
    });
  };

  const tryAgain = async () => {
    setHasError(false);
    setStackUpdateInProgress(false);
    setValidationInProgress(false);
    setLoadingStackArn(true);
    setTriggerFetchStackId(!triggerFetchStackId);
  };

  return (
    <PanelWrapper
      rounded
      header={{
        title: `Update permissions`,
        onClose,
      }}
    >
      <CardContent>
        <Stack direction='column'>
          <Box className='my-[8px]'>
            <b>Account</b>: {roleAccountId}
          </Box>
          <Box className='my-[8px]'>
            <b>Currently deployed stack</b>:{' '}
            {account.installedVersion ?? '1.0.0'}
            <br />
            <b>Latest version</b>: {account.latestVersion}
          </Box>
          <Box className='my-[8px]'>
            <b>Stack ARN</b>:
            <Stack alignItems='center' direction='row' gap='12px'>
              {isLoadingStackArn && <Loader variant='inline' />}
            </Stack>
            {!isLoadingStackArn && (
              <>
                <Box className='my-[8px]'>{stackState.stackId}</Box>
              </>
            )}
          </Box>
        </Stack>
        <Divider></Divider>
        <Box className='my-[15px]'>
          To get started, make sure you&apos;re logged in to account{' '}
          {roleAccountId} with an identity that can update CloudFormation
          stacks.
        </Box>
        <Box className='my-[15px]'>
          After opening the AWS console, select{' '}
          <b>Replace existing template &gt; Amazon S3 URL</b>, and paste this
          URL:
        </Box>
        <Box className='my-[8px]'>
          <CopyToClipboardButton textToCopy={templateUrl} />
          {templateUrl}
        </Box>
        <Box className='my-[15px]'>
          {!isStackUpdateInProgress && !hasError && (
            <Button
              variant='contained'
              className='px-[20px] mt-[20px] w-full'
              sx={{ height: 48 }}
              data-testid='open-aws-console'
              onClick={() => void openAWSConsole()}
              disabled={isLoadingStackArn}
            >
              Open AWS Console{' '}
              <i className='material-symbols-open-in-new w-[20px] h-[20px] ml-[8px]' />
            </Button>
          )}
          {isStackUpdateInProgress && !hasError && (
            <Box className='flex items-center gap-[8px] ml-[20px] justify-between'>
              <Stack alignItems='center' direction='row' gap='12px'>
                <Loader variant='inline' />
                <Typography variant='body2'>
                  Waiting for permissions...
                </Typography>
              </Stack>
              <Button
                variant='contained'
                data-testid='aws-console-done'
                disabled={isValidationInProgress}
                onClick={() => void onUpdateClicked()}
              >
                Update
              </Button>
            </Box>
          )}
          {hasError && (
            <Box
              className='flex items-center gap-[4px] ml-[20px] h-[48px]'
              sx={{ color: 'var(--mui-palette-error-main)' }}
            >
              <i className='material-symbols-close-rounded w-[20px] h-[20px]' />{' '}
              Something went wrong
              <Divider
                orientation='vertical'
                flexItem
                className='mx-[6px] my-auto h-[20px]'
              />
              <Button variant='contained' onClick={() => void tryAgain()}>
                Try Again
              </Button>
            </Box>
          )}
        </Box>
        <Typography className='my-[8px]'>
          <ReadMoreLink
            text='Read the full instructions'
            href={`${docsBaseUrl}/user-guide/connection/aws/permissions/aws-update-permissions`}
          />
        </Typography>
      </CardContent>
    </PanelWrapper>
  );
};
